import React, { useMemo } from 'react'
import { Box, InputBase, MenuItem, TextField, Typography } from '@material-ui/core'
import { useTheme } from '@material-ui/core/styles'
import HPOFilterWrapper from 'src/components/HPOFilterWrapper'
import FileNameText from 'src/components/FileNameText'
import { GroupLabel, Muted, TextLink, cellInputSx, MONO_SX } from './components/ui'
import TumourTypeSelect from './components/TumourTypeSelect'
import KitSelect, { kitMenuOptions } from './components/KitSelect'
import NormalSelect from './components/NormalSelect'
import IssueList from './components/IssueList'
import HpoSuggestions from './components/HpoSuggestions'
import { NOTES_MAX_LENGTH, libraryOf } from 'src/services/analysisStart'
import {
  SEX_OPTIONS,
  aiPatch,
  canSavePatient,
  canUseAI,
  describeFiles,
  effectiveAI,
  parseAge,
  sexShortLabel,
} from './model/samples'
import { isRnaWorkflow, isTumourNormal } from './model/workflow'
import { RNA_CAPTURE_OFF, validateLibrary } from 'src/views/AnalysisView/lib/rnaLibrary'
import LibraryFields from 'src/views/AnalysisView/AnnotationView/components/LibraryFields'
import { SPECIMEN_TYPES } from 'src/views/AnalysisView/lib/specimen'
import AnalysisSearchSelect from 'src/views/AnalysisView/AnnotationView/components/AnalysisSearchSelect'
import { toTerms, fromTerms } from './model/hpo'

// One sample's details: patient, phenotype (germline) or tumour (somatic). Used as
// the whole samples area when there is one sample, and inline under a table row.

const Heading = ({ children, hint }) => (
  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
    <Typography sx={{ fontSize: 14.5, fontWeight: 600 }}>{children}</Typography>
    {hint && <Muted component="span">{hint}</Muted>}
  </Box>
)

const Field = ({ label, children, width }) => (
  <Box sx={{ width: width || '100%', maxWidth: '100%', minWidth: 0 }}>
    {label && <GroupLabel sx={{ mb: 0.5 }}>{label}</GroupLabel>}
    {children}
  </Box>
)

// A saved value of a long-read file, which cannot be saved from here.
const ReadOnlyValue = ({ children, label }) => (
  <Typography
    aria-label={label}
    title="Saved on the file. Read-only here."
    sx={{ fontSize: 14.5, minHeight: 24, color: children ? 'text.primary' : 'text.secondary' }}
  >
    {children || 'Not set'}
  </Typography>
)

const textOf = (value) => (value === null || value === undefined ? '' : String(value))

const boxedInputSx = (theme) => ({
  ...cellInputSx(theme),
  borderColor: theme.palette.divider,
  backgroundColor: 'transparent',
  height: 36,
  fontSize: 14.5,
})

// An age in years. A text field, so arrow keys, the scroll wheel and a paste cannot
// quietly change it. The typed text is kept as is and checked by computeIssues
// (model/samples.js); on blur a valid entry such as "34,5" or "34 y" is written as a
// plain number.
export const NumberInput = ({ value, onChange, placeholder, sx, inputProps, error, disabled, onKeyDown, cell }) => {
  const theme = useTheme()
  const text = value === '' || value === null || value === undefined ? '' : String(value)
  const parsed = parseAge(text)
  const normalise = () => {
    if (typeof parsed === 'number' && !Number.isNaN(parsed) && String(parsed) !== text) onChange(String(parsed))
  }
  return (
    <InputBase
      type="text"
      value={text}
      onChange={(e) => onChange(e.target.value)}
      onBlur={normalise}
      onKeyDown={onKeyDown}
      placeholder={placeholder}
      error={error || (!disabled && Number.isNaN(parsed))}
      disabled={disabled}
      inputProps={{ inputMode: 'decimal', autoComplete: 'off', spellCheck: false, ...inputProps }}
      sx={{ ...(cell ? cellInputSx(theme) : boxedInputSx(theme)), ...sx }}
    />
  )
}

const selectSx = { width: '100%', '& .MuiInputBase-root': { fontSize: 14.5 } }

// A specimen type for the tumour or the normal; blank is "not recorded", never a negative.
const SpecimenTypeSelect = ({ value, onChange, label }) => (
  <TextField
    select
    size="small"
    value={value || ''}
    onChange={(e) => onChange(e.target.value)}
    SelectProps={{ displayEmpty: true }}
    inputProps={{ 'aria-label': label }}
    sx={selectSx}
  >
    <MenuItem value="" sx={{ fontSize: 14.5 }}>
      Not recorded
    </MenuItem>
    {SPECIMEN_TYPES.map((o) => (
      <MenuItem key={o.value} value={o.value} sx={{ fontSize: 14.5 }}>
        {o.label}
      </MenuItem>
    ))}
  </TextField>
)

// Somatic specimen types and the patient's other analyses. Only what cannot be
// inferred and changes how the analysis is read is asked; links start unverified.
const SpecimenFields = ({ sample, paired, onChange }) => {
  const specimen = sample.specimen || {}
  const set = (field) => (value) => onChange({ specimen: { ...specimen, [field]: value } })
  return (
    <>
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
        <Field label="Tumour specimen" width={210}>
          <SpecimenTypeSelect value={specimen.tumourType} onChange={set('tumourType')} label="Tumour specimen" />
        </Field>
      </Box>
      {paired && (
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          <Field label="Normal specimen" width={210}>
            <SpecimenTypeSelect value={specimen.normalType} onChange={set('normalType')} label="Normal specimen" />
          </Field>
        </Box>
      )}
      <Field label="Other analyses of this patient" width={440}>
        <AnalysisSearchSelect
          multiple
          value={sample.linkedAnalyses || []}
          onChange={(linkedAnalyses) => onChange({ linkedAnalyses })}
          label=""
          helperText={
            paired
              ? 'Optional. Analyses of the matched normal upload are linked automatically. Links start unverified.'
              : 'Optional: the matched normal or a germline analysis, or an RNA analysis, of the same patient. Links start unverified.'
          }
        />
      </Field>
    </>
  )
}

const SampleEditor = ({
  sample,
  workflow,
  fileMap,
  onChange,
  onRemove,
  onAddAnother,
  issues = [],
  onFix,
  normalOptions = [],
  inline = false,
  // Inline under a table row that has no Notes column (the AI layout).
  inlineNotes = false,
  // The /bed list: the RNA target panel picks from it as the DNA capture kit does.
  bedFiles,
  // rnaCaptureOf(GET rna-vocabulary): whether targeted RNA capture panels are accepted.
  rnaCapture = RNA_CAPTURE_OFF,
}) => {
  const theme = useTheme()
  const files = useMemo(() => describeFiles(sample, fileMap), [sample, fileMap])
  const somatic = workflow.analysis === 'somatic'
  const tn = isTumourNormal(workflow)
  const rna = isRnaWorkflow(workflow)
  // The DNA calling rule (own_calling) does not apply to an RNA target panel: the
  // RNA start has its own panel checks, so no RNA panel is greyed out by it.
  const panelOptions = useMemo(
    () => (rna ? kitMenuOptions(bedFiles).map((o) => ({ ...o, unavailableReason: '' })) : []),
    [rna, bedFiles],
  )
  const libraryErrors = rna ? validateLibrary(libraryOf(sample), rnaCapture) : {}

  const patientEditable = canSavePatient(sample)
  const setPatient = (field, value) =>
    onChange({ [field]: value, changed: { ...(sample.changed || {}), [field]: true } })
  const hpoTerms = useMemo(() => toTerms(sample.hpo), [sample.hpo])
  // "Clear" on an invalid age and "Run without AI" are handled here; the rest go to
  // the panel.
  const handleFix = (fixName, issue) => {
    if (fixName === 'clear' && issue?.field) setPatient(issue.field, '')
    else if (fixName === 'aiOff' || fixName === 'noAI') onChange(aiPatch(workflow, false))
    else if (onFix) onFix(fixName, issue)
  }
  // AI runs for this sample: HPO terms and clinical history are needed.
  const aiOn = effectiveAI(workflow, sample)
  // The sample's own AI choice differs from the batch's interpretation.
  const batchAI = workflow.interpretation === 'ai'
  const ownAIChoice = canUseAI(workflow, sample) && aiOn !== batchAI

  const stateText =
    files.state === 'ready'
      ? 'Verified'
      : files.state === 'verifying'
      ? 'Verifying'
      : files.state === 'unverified'
      ? 'Not verified'
      : ''

  return (
    <Box sx={{ maxWidth: 880 }}>
      {!inline && (
        <Box sx={{ mb: 3 }}>
          <FileNameText
            name={sample.key}
            variant="plain"
            maxLines={2}
            sx={{ fontSize: 20, fontWeight: 600, lineHeight: 1.3 }}
          />
          <Muted sx={{ mt: 0.5 }}>
            {[files.summary, stateText].filter(Boolean).join(' · ')}
            {files.names.length > 0 && (
              <Box component="span" sx={{ display: 'block', ...MONO_SX, fontSize: 12.5, mt: 0.25 }}>
                {files.names.join(', ')}
              </Box>
            )}
          </Muted>
          {issues.length > 0 && <IssueList issues={issues} onFix={handleFix} sx={{ mt: 1.5 }} />}
        </Box>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            md: inline ? `${inlineNotes ? 200 : 120}px minmax(0, 1fr)` : '320px minmax(0, 1fr)',
          },
          gap: 4,
          alignItems: 'start',
        }}
      >
        {inline ? (
          // The row already holds sex, age and (without the AI columns) notes.
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
            {!somatic && (
              <Field label="Age at onset" width={120}>
                {patientEditable ? (
                  <NumberInput
                    value={sample.onsetAge}
                    onChange={(v) => setPatient('onsetAge', v)}
                    placeholder="Years"
                    inputProps={{ 'aria-label': 'Age at onset' }}
                  />
                ) : (
                  <ReadOnlyValue label="Age at onset">{textOf(sample.onsetAge)}</ReadOnlyValue>
                )}
              </Field>
            )}
            {inlineNotes && (
              <Field label="Notes">
                {patientEditable ? (
                  <TextField
                    multiline
                    minRows={2}
                    maxRows={6}
                    size="small"
                    value={sample.notes || ''}
                    onChange={(e) => setPatient('notes', e.target.value)}
                    inputProps={{ maxLength: NOTES_MAX_LENGTH, 'aria-label': `Notes for ${sample.key}` }}
                    sx={{ width: '100%', '& .MuiInputBase-root': { fontSize: 14.5 } }}
                  />
                ) : (
                  <ReadOnlyValue label="Notes">{sample.notes || ''}</ReadOnlyValue>
                )}
              </Field>
            )}
          </Box>
        ) : !patientEditable ? (
          // Long-read files: their saved details, read-only.
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Heading hint="Saved on the file">Patient</Heading>
            <Field label="Sex">
              <ReadOnlyValue label="Sex">{sample.sex ? sexShortLabel(sample.sex) : ''}</ReadOnlyValue>
            </Field>
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <Field label="Age">
                <ReadOnlyValue label="Age">{textOf(sample.age)}</ReadOnlyValue>
              </Field>
              <Field label="Age at onset">
                <ReadOnlyValue label="Age at onset">{textOf(sample.onsetAge)}</ReadOnlyValue>
              </Field>
            </Box>
            <Field label="Notes">
              <ReadOnlyValue label="Notes">{sample.notes || ''}</ReadOnlyValue>
            </Field>
            <Muted>Read-only here.</Muted>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Heading hint="Optional">Patient</Heading>
            {/* A somatic run reads no age and infers sex from the reads. */}
            {!somatic && (
              <>
                <Field label="Sex">
                  <TextField
                    select
                    size="small"
                    value={sample.sex || ''}
                    onChange={(e) => setPatient('sex', e.target.value)}
                    SelectProps={{ displayEmpty: true }}
                    sx={{ width: '100%', '& .MuiInputBase-root': { fontSize: 14.5 } }}
                  >
                    {SEX_OPTIONS.map((o) => (
                      <MenuItem key={o.value || 'unknown'} value={o.value} sx={{ fontSize: 14.5 }}>
                        {o.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Field>
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  <Field label="Age">
                    <NumberInput
                      value={sample.age}
                      onChange={(v) => setPatient('age', v)}
                      placeholder="Years"
                      inputProps={{ 'aria-label': 'Age' }}
                    />
                  </Field>
                  <Field label="Age at onset">
                    <NumberInput
                      value={sample.onsetAge}
                      onChange={(v) => setPatient('onsetAge', v)}
                      placeholder="Years"
                      inputProps={{ 'aria-label': 'Age at onset' }}
                    />
                  </Field>
                </Box>
              </>
            )}
            <Field label="Notes">
              <TextField
                multiline
                minRows={2}
                maxRows={6}
                size="small"
                value={sample.notes || ''}
                onChange={(e) => setPatient('notes', e.target.value)}
                inputProps={{ maxLength: NOTES_MAX_LENGTH }}
                sx={{ width: '100%', '& .MuiInputBase-root': { fontSize: 14.5 } }}
              />
            </Field>
          </Box>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
          {!inline && (
            <Heading hint={somatic || !aiOn ? 'Optional' : 'Needed for AI'}>{somatic ? 'Tumour' : 'Phenotype'}</Heading>
          )}
          {somatic ? (
            <>
              <Field label="Tumour type" width={inline ? '100%' : 440}>
                <TumourTypeSelect
                  id={`tumour-input-${sample.uid}`}
                  value={sample.tumourType}
                  onChange={(tumourType) => onChange({ tumourType })}
                  placeholder={workflow.tumourType ? `Batch default: ${workflow.tumourType.name}` : 'None'}
                />
              </Field>
              {tn && !inline && (
                <Field label="Normal sample" width={440}>
                  <NormalSelect sample={sample} options={normalOptions} onChange={onChange} />
                </Field>
              )}
              {!inline && <SpecimenFields sample={sample} paired={tn} onChange={onChange} />}
              {!inline && rna && (
                <Field label="RNA library" width={560}>
                  <LibraryFields
                    library={libraryOf(sample)}
                    onChange={(library) => onChange({ library })}
                    errors={libraryErrors}
                    linkedAnalyses={sample.linkedAnalyses || []}
                    capture={rnaCapture}
                    panelPicker={
                      <KitSelect
                        value={libraryOf(sample).targetPanel}
                        options={panelOptions}
                        onChange={(targetPanel) => onChange({ library: { ...libraryOf(sample), targetPanel } })}
                        placeholder="Choose the panel"
                        error={!!libraryErrors.targetPanel}
                        helperText={libraryErrors.targetPanel || 'The panel the library was captured with.'}
                        sx={{ width: '100%' }}
                      />
                    }
                  />
                </Field>
              )}
            </>
          ) : (
            <>
              <Field label="Phenotype (HPO)">
                <Box sx={{ maxWidth: 520 }}>
                  <HPOFilterWrapper
                    value={hpoTerms}
                    onChange={({ hpoTerms: terms }) => onChange({ hpo: fromTerms(terms) })}
                    arrayOutput
                    label=""
                    compact
                    inputId={`hpo-input-${sample.uid}`}
                  />
                </Box>
              </Field>
              <Field label="Clinical history">
                <TextField
                  multiline
                  minRows={inline ? 3 : 5}
                  maxRows={12}
                  size="small"
                  value={sample.clinicalHistory || ''}
                  onChange={(e) => onChange({ clinicalHistory: e.target.value })}
                  placeholder="Symptoms, family history, previous tests"
                  inputProps={{ id: `history-details-${sample.uid}` }}
                  sx={{ width: '100%', '& .MuiInputBase-root': { fontSize: 14.5, lineHeight: 1.5 } }}
                />
                <HpoSuggestions
                  text={sample.clinicalHistory}
                  onTextChange={(clinicalHistory) => onChange({ clinicalHistory })}
                  hpo={sample.hpo}
                  onChange={(hpo) => onChange({ hpo })}
                  scope={sample.uid}
                  label={sample.key}
                />
              </Field>
              {aiOn && (
                <Field label="Guidance for AI">
                  <TextField
                    multiline
                    minRows={2}
                    maxRows={6}
                    size="small"
                    value={sample.aiGuidance || ''}
                    onChange={(e) => onChange({ aiGuidance: e.target.value })}
                    placeholder="Optional. For example: focus on cardiomyopathy genes"
                    sx={{ width: '100%', '& .MuiInputBase-root': { fontSize: 14.5 } }}
                  />
                </Field>
              )}
              {ownAIChoice && !aiOn && (
                <Muted>
                  Runs without AI.{' '}
                  <TextLink onClick={() => onChange(aiPatch(workflow, true))}>Use AI for this sample</TextLink>
                </Muted>
              )}
              {ownAIChoice && aiOn && (
                <Muted>
                  AI-assisted for this sample.{' '}
                  <TextLink onClick={() => onChange(aiPatch(workflow, false))}>Run without AI</TextLink>
                </Muted>
              )}
            </>
          )}
        </Box>
      </Box>

      {!inline && (
        <Box sx={{ mt: 4, display: 'flex', gap: 3, color: theme.palette.text.secondary }}>
          {onAddAnother && <TextLink onClick={onAddAnother}>Add another sample</TextLink>}
          {onRemove && (
            <TextLink tone="muted" onClick={onRemove}>
              Remove sample
            </TextLink>
          )}
        </Box>
      )}
    </Box>
  )
}

export default SampleEditor
